import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Truck, Plus, Pencil, Trash2, X, Globe, ChevronDown, Save, MapPin, Package } from 'lucide-react'
import { shippingService } from '@/services/shippingService'
import { categoriesService } from '@/services/categoriesService'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { formatCurrency, getLocalizedField } from '@/utils/formatters'

// Flattens the category tree into a depth-tagged list for the assign-categories checklist.
function flattenCategoryTree(nodes, lang, depth = 0, out = []) {
  nodes?.forEach((c) => {
    out.push({ id: c.id, name: getLocalizedField(c.name, lang) || c.slug, depth })
    if (c.children?.length) flattenCategoryTree(c.children, lang, depth + 1, out)
  })
  return out
}

function DialogOverlay({ children, onClose }) {
  return (
    <div style={{ position:'fixed',inset:0,zIndex:50,display:'flex',alignItems:'center',justifyContent:'center',padding:16,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(4px)' }} onClick={onClose}>
      <div className="animate-in" style={{ background:'#fff',borderRadius:24,padding:28,width:'100%',maxWidth:480,boxShadow:'0 32px 64px rgba(0,0,0,0.2)',position:'relative',maxHeight:'90vh',overflowY:'auto' }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={{ position:'absolute',top:16,left:16,width:32,height:32,borderRadius:10,background:'#F3F3F3',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#767683' }}><X size={16}/></button>
        {children}
      </div>
    </div>
  )
}

const ZONE_EMPTY  = { name:'', governorates:[], isActive:true }
const METHOD_EMPTY = { zoneId:'', nameAr:'', nameEn:'', descAr:'', descEn:'', price:'', freeShippingThreshold:'', estimatedDays:'', isActive:true }
const CLASS_EMPTY = { nameAr:'', nameEn:'', baseCost:'', extraUnitCost:'', maxCost:'', isActive:true }

export default function ShippingPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState('zones')

  // Zones
  const [zoneDialog,   setZoneDialog]   = useState({ open:false, mode:'create', zone:null })
  const [zoneForm,     setZoneForm]     = useState(ZONE_EMPTY)
  const [delZone,      setDelZone]      = useState({ open:false, zone:null })

  // Methods
  const [methodDialog, setMethodDialog] = useState({ open:false, mode:'create', method:null })
  const [methodForm,   setMethodForm]   = useState(METHOD_EMPTY)
  const [delMethod,    setDelMethod]    = useState({ open:false, method:null })

  // Shipping classes
  const [classDialog, setClassDialog] = useState({ open:false, mode:'create', cls:null })
  const [classForm,   setClassForm]   = useState(CLASS_EMPTY)
  const [delClass,    setDelClass]    = useState({ open:false, cls:null })
  const [classCategoryIds, setClassCategoryIds] = useState([])

  const { data: catTree } = useQuery({ queryKey:['categories-tree'], queryFn: categoriesService.getTree, enabled: classDialog.open })
  const flatCats = flattenCategoryTree(catTree, lang)

  const { data: assignedCats } = useQuery({
    queryKey: ['shipping-class-categories', classDialog.cls?.id],
    queryFn: () => shippingService.getClassCategories(classDialog.cls.id),
    enabled: classDialog.open && classDialog.mode === 'edit' && !!classDialog.cls?.id,
  })
  useEffect(() => {
    if (classDialog.open && classDialog.mode === 'edit' && assignedCats) {
      setClassCategoryIds(assignedCats.map((c) => c.id))
    }
  }, [classDialog.open, classDialog.mode, assignedCats])

  const toggleClassCategory = (id) => setClassCategoryIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id])

  const { data: govCatalogData } = useQuery({ queryKey:['governorate-catalog'], queryFn: shippingService.getGovernorateCatalog, staleTime: Infinity })
  const govCatalog = govCatalogData || []
  const govName = (code) => govCatalog.find((g) => g.code === code)?.[lang === 'ar' ? 'ar' : 'en'] ?? code

  const { data: zones,   isLoading: zonesLoading }   = useQuery({ queryKey:['shipping-zones'],   queryFn: shippingService.getZones })
  const { data: methods, isLoading: methodsLoading }  = useQuery({ queryKey:['shipping-methods'], queryFn: () => shippingService.getMethods() })
  const { data: classes, isLoading: classesLoading }  = useQuery({ queryKey:['shipping-classes'], queryFn: shippingService.getClasses })

  const zonesList   = zones   || []
  const methodsList = methods || []
  const classesList = classes || []

  const inv = () => { qc.invalidateQueries({ queryKey:['shipping-zones'] }); qc.invalidateQueries({ queryKey:['shipping-methods'] }); qc.invalidateQueries({ queryKey:['shipping-classes'] }) }

  const createZone  = useMutation({ mutationFn: (d) => shippingService.createZone(d),           onSuccess: () => { toast.success(t('shipping.zoneCreated')); inv(); setZoneDialog({ open:false,mode:'create',zone:null }); setZoneForm(ZONE_EMPTY) }, onError: () => toast.error(t('shipping.zoneCreateFailed')) })
  const updateZone  = useMutation({ mutationFn: ({ id,d }) => shippingService.updateZone(id,d), onSuccess: () => { toast.success(t('shipping.zoneUpdated')); inv(); setZoneDialog({ open:false,mode:'create',zone:null }) }, onError: () => toast.error(t('shipping.updateFailed')) })
  const deleteZone  = useMutation({ mutationFn: (id) => shippingService.deleteZone(id),         onSuccess: () => { toast.success(t('shipping.deleted')); inv(); setDelZone({ open:false,zone:null }) }, onError: () => toast.error(t('shipping.deleteFailed')) })

  const createMethod = useMutation({ mutationFn: (d) => shippingService.createMethod(d),              onSuccess: () => { toast.success(t('shipping.methodCreated')); inv(); setMethodDialog({ open:false,mode:'create',method:null }); setMethodForm(METHOD_EMPTY) }, onError: () => toast.error(t('shipping.createFailed')) })
  const updateMethod = useMutation({ mutationFn: ({ id,d }) => shippingService.updateMethod(id,d),    onSuccess: () => { toast.success(t('shipping.updated')); inv(); setMethodDialog({ open:false,mode:'create',method:null }) }, onError: () => toast.error(t('shipping.updateFailed')) })
  const deleteMethod = useMutation({ mutationFn: (id) => shippingService.deleteMethod(id),            onSuccess: () => { toast.success(t('shipping.deleted')); inv(); setDelMethod({ open:false,method:null }) }, onError: () => toast.error(t('shipping.deleteFailed')) })

  const createClass = useMutation({
    mutationFn: async (d) => {
      const created = await shippingService.createClass(d)
      if (classCategoryIds.length > 0) await shippingService.setClassCategories(created.id, classCategoryIds)
      return created
    },
    onSuccess: () => {
      toast.success(t('shipping.classCreated')); inv()
      qc.invalidateQueries({ queryKey:['categories-tree'] })
      setClassDialog({ open:false,mode:'create',cls:null }); setClassForm(CLASS_EMPTY); setClassCategoryIds([])
    },
    onError: (e) => toast.error(e?.response?.data?.message || t('shipping.createFailed')),
  })
  const updateClass = useMutation({
    mutationFn: async ({ id, d, categoryIds }) => {
      const updated = await shippingService.updateClass(id, d)
      await shippingService.setClassCategories(id, categoryIds)
      return updated
    },
    onSuccess: () => {
      toast.success(t('shipping.updated')); inv()
      qc.invalidateQueries({ queryKey:['categories-tree'] })
      setClassDialog({ open:false,mode:'create',cls:null })
    },
    onError: (e) => toast.error(e?.response?.data?.message || t('shipping.updateFailed')),
  })
  const deleteClass = useMutation({ mutationFn: (id) => shippingService.deleteClass(id),         onSuccess: () => { toast.success(t('shipping.deleted')); inv(); setDelClass({ open:false,cls:null }) }, onError: (e) => toast.error(e?.response?.data?.message || t('shipping.deleteFailed')) })

  const openEditZone = (zone) => {
    setZoneForm({ name: zone.name||'', governorates: zone.governorates||[], isActive: zone.isActive })
    setZoneDialog({ open:true, mode:'edit', zone })
  }

  const toggleGovernorate = (code) => setZoneForm((p) => ({
    ...p,
    governorates: p.governorates.includes(code)
      ? p.governorates.filter((g) => g !== code)
      : [...p.governorates, code],
  }))

  const openEditMethod = (m) => {
    setMethodForm({ zoneId:m.zone?.id||'', nameAr:m.name?.ar||'', nameEn:m.name?.en||'', descAr:m.description?.ar||'', descEn:m.description?.en||'', price:m.price||'', freeShippingThreshold:m.freeShippingThreshold||'', estimatedDays:m.estimatedDays||'', isActive:m.isActive })
    setMethodDialog({ open:true, mode:'edit', method:m })
  }

  const openEditClass = (c) => {
    setClassForm({ nameAr:c.name?.ar||'', nameEn:c.name?.en||'', baseCost:c.baseCost||'', extraUnitCost:c.extraUnitCost??'', maxCost:c.maxCost||'', isActive:c.isActive })
    setClassCategoryIds([])
    setClassDialog({ open:true, mode:'edit', cls:c })
  }

  const submitClass = () => {
    const payload = {
      name: { ar: classForm.nameAr, en: classForm.nameEn || classForm.nameAr },
      baseCost: parseFloat(classForm.baseCost) || 0,
      extraUnitCost: classForm.extraUnitCost !== '' ? parseFloat(classForm.extraUnitCost) : 0,
      maxCost: classForm.maxCost !== '' ? parseFloat(classForm.maxCost) : (classDialog.mode==='edit' ? null : undefined),
      isActive: classForm.isActive,
    }
    if (classDialog.mode === 'create') createClass.mutate(payload)
    else updateClass.mutate({ id: classDialog.cls.id, d: payload, categoryIds: classCategoryIds })
  }

  const submitZone = () => {
    if (zoneForm.governorates.length === 0) { toast.error(t('shipping.selectAtLeastOneGovernorate')); return }
    const payload = { name: zoneForm.name, governorates: zoneForm.governorates, isActive: zoneForm.isActive }
    if (zoneDialog.mode === 'create') createZone.mutate(payload)
    else updateZone.mutate({ id: zoneDialog.zone.id, d: payload })
  }

  const submitMethod = () => {
    const payload = { zoneId: methodForm.zoneId, name:{ ar:methodForm.nameAr, en:methodForm.nameEn }, description:{ ar:methodForm.descAr, en:methodForm.descEn }, price: parseFloat(methodForm.price)||0, freeShippingThreshold: methodForm.freeShippingThreshold ? parseFloat(methodForm.freeShippingThreshold) : null, estimatedDays: methodForm.estimatedDays, isActive: methodForm.isActive }
    if (methodDialog.mode === 'create') createMethod.mutate(payload)
    else updateMethod.mutate({ id: methodDialog.method.id, d: payload })
  }

  return (
    <div className="space-y-5">
      {/* Tabs */}
      <div className="hg-card p-1 flex gap-1">
        {[{ key:'zones',label:t('shipping.zonesTab'),icon:Globe },{ key:'methods',label:t('shipping.methodsTab'),icon:Truck },{ key:'classes',label:t('shipping.classesTab'),icon:Package }].map(({ key,label,icon:Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            style={{ display:'flex',alignItems:'center',gap:8,padding:'8px 20px',borderRadius:12,fontSize:13,fontWeight:700,fontFamily:'Cairo, sans-serif',border:'none',cursor:'pointer',transition:'all 0.2s', background: activeTab===key ? '#000666' : 'transparent', color: activeTab===key ? '#fff' : '#767683', boxShadow: activeTab===key ? '0 4px 12px rgba(0,6,102,0.25)' : 'none' }}>
            <Icon size={15}/>{label}
          </button>
        ))}
      </div>

      {activeTab === 'zones' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => { setZoneForm(ZONE_EMPTY); setZoneDialog({ open:true,mode:'create',zone:null }) }}
              style={{ display:'flex',alignItems:'center',gap:8,padding:'10px 20px',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,color:'#fff',background:'#000666',border:'none',cursor:'pointer',boxShadow:'0 8px 24px rgba(26,35,126,0.2)' }}>
              <Plus size={16}/>{t('shipping.addZone')}
            </button>
          </div>
          {zonesLoading ? <div className="flex justify-center py-16"><LoadingSpinner/></div>
            : zonesList.length === 0 ? <div className="hg-card"><EmptyState icon={Globe} message={t('shipping.noZones')}/></div>
            : (
              <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))',gap:16 }}>
                {zonesList.map((zone) => (
                  <div key={zone.id} style={{ background:'#fff',borderRadius:18,padding:20,boxShadow:'0 2px 4px rgba(26,28,28,0.04),0 8px 16px rgba(26,28,28,0.06)' }}>
                    <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12 }}>
                      <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                        <div style={{ width:38,height:38,borderRadius:12,background:'rgba(0,6,102,0.08)',color:'#000666',display:'flex',alignItems:'center',justifyContent:'center' }}><Globe size={18}/></div>
                        <div>
                          <div style={{ fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:15,color:'#1A1C1C' }}>{zone.name}</div>
                          <div style={{ fontSize:11,color: zone.isActive?'#1B6D24':'#767683',fontWeight:700,fontFamily:'Cairo, sans-serif' }}>{zone.isActive?t('common.active'):t('common.inactive')}</div>
                        </div>
                      </div>
                      <div style={{ display:'flex',gap:6 }}>
                        <button onClick={() => openEditZone(zone)} style={{ width:30,height:30,borderRadius:8,background:'rgba(0,6,102,0.06)',color:'#000666',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}><Pencil size={13}/></button>
                        <button onClick={() => setDelZone({ open:true,zone })} style={{ width:30,height:30,borderRadius:8,background:'rgba(186,26,26,0.06)',color:'#BA1A1A',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}><Trash2 size={13}/></button>
                      </div>
                    </div>
                    <div style={{ display:'flex',flexWrap:'wrap',gap:5 }}>
                      {(zone.governorates||[]).map((c) => (
                        <span key={c} style={{ fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:6,background:'rgba(0,6,102,0.06)',color:'#000666',fontFamily:'Tajawal, sans-serif' }}>{govName(c)}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {activeTab === 'methods' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => { setMethodForm(METHOD_EMPTY); setMethodDialog({ open:true,mode:'create',method:null }) }}
              style={{ display:'flex',alignItems:'center',gap:8,padding:'10px 20px',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,color:'#fff',background:'#000666',border:'none',cursor:'pointer',boxShadow:'0 8px 24px rgba(26,35,126,0.2)' }}>
              <Plus size={16}/>{t('shipping.addMethod')}
            </button>
          </div>
          {methodsLoading ? <div className="flex justify-center py-16"><LoadingSpinner/></div>
            : methodsList.length === 0 ? <div className="hg-card"><EmptyState icon={Truck} message={t('shipping.noMethods')}/></div>
            : (
              <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))',gap:16 }}>
                {methodsList.map((m) => (
                  <div key={m.id} style={{ background:'#fff',borderRadius:18,padding:20,boxShadow:'0 2px 4px rgba(26,28,28,0.04),0 8px 16px rgba(26,28,28,0.06)' }}>
                    <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:10 }}>
                      <div>
                        <div style={{ fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:15,color:'#1A1C1C',marginBottom:2 }}>{getLocalizedField(m.name, lang)}</div>
                        <div style={{ fontSize:12,color:'#767683',fontFamily:'Tajawal, sans-serif' }}>{getLocalizedField(m.description, lang)}</div>
                      </div>
                      <div style={{ display:'flex',gap:6 }}>
                        <button onClick={() => openEditMethod(m)} style={{ width:30,height:30,borderRadius:8,background:'rgba(0,6,102,0.06)',color:'#000666',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}><Pencil size={13}/></button>
                        <button onClick={() => setDelMethod({ open:true,method:m })} style={{ width:30,height:30,borderRadius:8,background:'rgba(186,26,26,0.06)',color:'#BA1A1A',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}><Trash2 size={13}/></button>
                      </div>
                    </div>
                    <div style={{ display:'flex',gap:12,flexWrap:'wrap' }}>
                      <span style={{ fontSize:13,fontWeight:700,fontFamily:'Cairo, sans-serif',color:'#FF6D00' }}>{formatCurrency(m.price)}</span>
                      {m.freeShippingThreshold && <span style={{ fontSize:12,color:'#1B6D24',fontFamily:'Tajawal, sans-serif' }}>{t('shipping.freeShippingOver', { amount: formatCurrency(m.freeShippingThreshold) })}</span>}
                      {m.estimatedDays && <span style={{ fontSize:12,color:'#767683',fontFamily:'Tajawal, sans-serif' }}>⏱ {t('shipping.daysUnit', { days: m.estimatedDays })}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {activeTab === 'classes' && (
        <div className="space-y-4">
          <div className="hg-card p-4" style={{ background:'rgba(0,6,102,0.03)' }}>
            <p style={{ fontFamily:'Tajawal, sans-serif',fontSize:13,color:'#454652',margin:0,lineHeight:1.8 }}>
              {t('shipping.classesInfoBefore')}
              <b> {t('shipping.classesInfoBold')}</b>{t('shipping.classesInfoAfter')}
            </p>
          </div>
          <div className="flex justify-end">
            <button onClick={() => { setClassForm(CLASS_EMPTY); setClassCategoryIds([]); setClassDialog({ open:true,mode:'create',cls:null }) }}
              style={{ display:'flex',alignItems:'center',gap:8,padding:'10px 20px',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,color:'#fff',background:'#000666',border:'none',cursor:'pointer',boxShadow:'0 8px 24px rgba(26,35,126,0.2)' }}>
              <Plus size={16}/>{t('shipping.addClass')}
            </button>
          </div>
          {classesLoading ? <div className="flex justify-center py-16"><LoadingSpinner/></div>
            : classesList.length === 0 ? <div className="hg-card"><EmptyState icon={Package} message={t('shipping.noClasses')}/></div>
            : (
              <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))',gap:16 }}>
                {classesList.map((c) => (
                  <div key={c.id} style={{ background:'#fff',borderRadius:18,padding:20,boxShadow:'0 2px 4px rgba(26,28,28,0.04),0 8px 16px rgba(26,28,28,0.06)' }}>
                    <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12 }}>
                      <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                        <div style={{ width:38,height:38,borderRadius:12,background:'rgba(255,109,0,0.08)',color:'#FF6D00',display:'flex',alignItems:'center',justifyContent:'center' }}><Package size={18}/></div>
                        <div>
                          <div style={{ fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:15,color:'#1A1C1C' }}>{getLocalizedField(c.name, lang)}</div>
                          <div style={{ fontSize:11,color: c.isActive?'#1B6D24':'#767683',fontWeight:700,fontFamily:'Cairo, sans-serif',marginTop:2 }}>
                            {c.isActive ? t('common.active') : t('common.inactive')}
                            {' · '}
                            <span style={{ color: '#000666', fontWeight: 800 }}>
                              {c._count?.effectiveProducts ?? c._count?.products ?? 0} منتج مشمول
                            </span>
                            {' '}
                            <span style={{ fontWeight: 500, color: '#767683' }}>
                              ({c._count?.products ?? 0} مباشر + {c._count?.inheritedProducts ?? 0} من الأقسام)
                            </span>
                            {' · '}
                            <span style={{ color: '#FF6D00' }}>
                              {c._count?.categories ?? 0} فئة
                            </span>
                          </div>
                        </div>
                      </div>
                      <div style={{ display:'flex',gap:6 }}>
                        <button onClick={() => openEditClass(c)} style={{ width:30,height:30,borderRadius:8,background:'rgba(0,6,102,0.06)',color:'#000666',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}><Pencil size={13}/></button>
                        <button onClick={() => setDelClass({ open:true,cls:c })} style={{ width:30,height:30,borderRadius:8,background:'rgba(186,26,26,0.06)',color:'#BA1A1A',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}><Trash2 size={13}/></button>
                      </div>
                    </div>
                    <div style={{ display:'flex',gap:12,flexWrap:'wrap' }}>
                      <span style={{ fontSize:13,fontWeight:700,fontFamily:'Cairo, sans-serif',color:'#FF6D00' }}>{t('shipping.firstUnitCost', { amount: formatCurrency(c.baseCost) })}</span>
                      <span style={{ fontSize:12,color:'#454652',fontFamily:'Tajawal, sans-serif' }}>{t('shipping.increaseCost', { amount: formatCurrency(c.extraUnitCost || 0) })}</span>
                      {c.maxCost && <span style={{ fontSize:12,color:'#1B6D24',fontFamily:'Tajawal, sans-serif' }}>{t('shipping.maxCostDisplay', { amount: formatCurrency(c.maxCost) })}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {/* Zone dialog */}
      {zoneDialog.open && (
        <DialogOverlay onClose={() => setZoneDialog({ open:false,mode:'create',zone:null })}>
          <h3 style={{ fontFamily:'Cairo, sans-serif',fontWeight:900,fontSize:18,color:'#1A1C1C',marginBottom:20 }}>{zoneDialog.mode==='create'?t('shipping.addZoneTitle'):t('shipping.editZoneTitle')}</h3>
          <label style={LBL}>{t('shipping.zoneNameLabel')}</label>
          <input value={zoneForm.name} onChange={(e) => setZoneForm((p) => ({ ...p,name:e.target.value }))} placeholder={t('shipping.zoneNamePlaceholder')} style={INP} />
          <label style={{ ...LBL,marginTop:12 }}>
            {t('shipping.governoratesLabel', { count: zoneForm.governorates.length })}
          </label>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(120px, 1fr))',gap:6,maxHeight:220,overflowY:'auto',padding:8,borderRadius:10,background:'#F8F8FA' }}>
            {govCatalog.map((g) => {
              const on = zoneForm.governorates.includes(g.code)
              return (
                <button
                  key={g.code}
                  type="button"
                  onClick={() => toggleGovernorate(g.code)}
                  style={{
                    display:'flex',alignItems:'center',gap:6,padding:'6px 8px',borderRadius:8,
                    border:on?'1.5px solid #000666':'1.5px solid #E8E8EE',
                    background:on?'rgba(0,6,102,0.06)':'#fff',
                    color:on?'#000666':'#767683',cursor:'pointer',
                    fontFamily:'Tajawal, sans-serif',fontSize:12,fontWeight:on?700:500,
                  }}
                >
                  <input type="checkbox" checked={on} readOnly style={{ pointerEvents:'none',width:13,height:13 }} />
                  {lang === 'ar' ? g.ar : (g.en || g.ar)}
                </button>
              )
            })}
          </div>
          <div style={{ display:'flex',alignItems:'center',gap:10,margin:'14px 0' }}>
            <input type="checkbox" checked={zoneForm.isActive} onChange={(e) => setZoneForm((p) => ({ ...p,isActive:e.target.checked }))} id="za" style={{ width:16,height:16 }}/>
            <label htmlFor="za" style={{ fontFamily:'Tajawal, sans-serif',fontSize:14,color:'#454652',cursor:'pointer' }}>{t('common.active')}</label>
          </div>
          <div style={{ display:'flex',gap:10,marginTop:4 }}>
            <button onClick={submitZone} disabled={createZone.isPending||updateZone.isPending} style={{ ...BTN, background:'#000666',color:'#fff' }}><Save size={15}/>{createZone.isPending||updateZone.isPending?'...':t('common.save')}</button>
            <button onClick={() => setZoneDialog({ open:false,mode:'create',zone:null })} style={{ ...BTN, background:'#F3F3F3',color:'#454652' }}>{t('common.cancel')}</button>
          </div>
        </DialogOverlay>
      )}

      {/* Method dialog */}
      {methodDialog.open && (
        <DialogOverlay onClose={() => setMethodDialog({ open:false,mode:'create',method:null })}>
          <h3 style={{ fontFamily:'Cairo, sans-serif',fontWeight:900,fontSize:18,color:'#1A1C1C',marginBottom:20 }}>{methodDialog.mode==='create'?t('shipping.addMethodTitle'):t('shipping.editMethodTitle')}</h3>
          <label style={LBL}>{t('shipping.zoneLabel')}</label>
          <select value={methodForm.zoneId} onChange={(e) => setMethodForm((p) => ({ ...p,zoneId:e.target.value }))} style={{ ...INP,cursor:'pointer' }}>
            <option value="">{t('shipping.selectZonePlaceholder')}</option>
            {zonesList.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginTop:12 }}>
            {[['nameAr',t('shipping.nameArLabel'),''],['nameEn',t('shipping.nameEnLabel'),'ltr'],['descAr',t('shipping.descArLabel'),''],['descEn',t('shipping.descEnLabel'),'ltr']].map(([k,l,d]) => (
              <div key={k}>
                <label style={LBL}>{l}</label>
                <input value={methodForm[k]} onChange={(e) => setMethodForm((p) => ({ ...p,[k]:e.target.value }))} dir={d||undefined} style={INP}/>
              </div>
            ))}
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginTop:12 }}>
            {[['price',t('shipping.priceLabel')],['freeShippingThreshold',t('shipping.freeShippingThresholdLabel')],['estimatedDays',t('shipping.estimatedDaysLabel')]].map(([k,l]) => (
              <div key={k}>
                <label style={LBL}>{l}</label>
                <input value={methodForm[k]} onChange={(e) => setMethodForm((p) => ({ ...p,[k]:e.target.value }))} type={k==='estimatedDays'?'text':'number'} min="0" step="0.01" style={INP}/>
              </div>
            ))}
          </div>
          <div style={{ display:'flex',alignItems:'center',gap:10,margin:'14px 0' }}>
            <input type="checkbox" checked={methodForm.isActive} onChange={(e) => setMethodForm((p) => ({ ...p,isActive:e.target.checked }))} id="ma"/>
            <label htmlFor="ma" style={{ fontFamily:'Tajawal, sans-serif',fontSize:14,color:'#454652',cursor:'pointer' }}>{t('common.active')}</label>
          </div>
          <div style={{ display:'flex',gap:10 }}>
            <button onClick={submitMethod} disabled={createMethod.isPending||updateMethod.isPending} style={{ ...BTN, background:'#000666',color:'#fff' }}><Save size={15}/>{createMethod.isPending||updateMethod.isPending?'...':t('common.save')}</button>
            <button onClick={() => setMethodDialog({ open:false,mode:'create',method:null })} style={{ ...BTN, background:'#F3F3F3',color:'#454652' }}>{t('common.cancel')}</button>
          </div>
        </DialogOverlay>
      )}

      {/* Shipping class dialog */}
      {classDialog.open && (
        <DialogOverlay onClose={() => setClassDialog({ open:false,mode:'create',cls:null })}>
          <h3 style={{ fontFamily:'Cairo, sans-serif',fontWeight:900,fontSize:18,color:'#1A1C1C',marginBottom:20 }}>{classDialog.mode==='create'?t('shipping.addClassTitle'):t('shipping.editClassTitle')}</h3>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10 }}>
            <div>
              <label style={LBL}>{t('shipping.nameArLabel')}</label>
              <input value={classForm.nameAr} onChange={(e) => setClassForm((p) => ({ ...p,nameAr:e.target.value }))} placeholder={t('shipping.classNameArPlaceholder')} style={INP}/>
            </div>
            <div>
              <label style={LBL}>{t('shipping.nameEnLabel')}</label>
              <input value={classForm.nameEn} onChange={(e) => setClassForm((p) => ({ ...p,nameEn:e.target.value }))} placeholder="e.g. Bulky items" dir="ltr" style={INP}/>
            </div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginTop:12 }}>
            <div>
              <label style={LBL}>{t('shipping.baseCostLabel')}</label>
              <input type="number" min="0" step="0.01" value={classForm.baseCost} onChange={(e) => setClassForm((p) => ({ ...p,baseCost:e.target.value }))} placeholder="500" dir="ltr" style={INP}/>
            </div>
            <div>
              <label style={LBL}>{t('shipping.extraUnitCostLabel')}</label>
              <input type="number" min="0" step="0.01" value={classForm.extraUnitCost} onChange={(e) => setClassForm((p) => ({ ...p,extraUnitCost:e.target.value }))} placeholder={t('shipping.extraUnitCostPlaceholder')} dir="ltr" style={INP}/>
            </div>
            <div>
              <label style={LBL}>{t('shipping.maxCostLabel')}</label>
              <input type="number" min="0" step="0.01" value={classForm.maxCost} onChange={(e) => setClassForm((p) => ({ ...p,maxCost:e.target.value }))} placeholder={t('shipping.maxCostPlaceholder')} dir="ltr" style={INP}/>
            </div>
          </div>
          <p style={{ fontFamily:'Tajawal, sans-serif',fontSize:11.5,color:'#767683',margin:'10px 0 0',lineHeight:1.7 }}>
            {t('shipping.classExampleText')}
          </p>

          <label style={{ ...LBL,marginTop:16 }}>{t('shipping.assignedCategoriesLabel', { count: classCategoryIds.length })}</label>
          <div style={{ maxHeight:200,overflowY:'auto',border:'1.5px solid #EBEBEB',borderRadius:10,padding:6 }}>
            {flatCats.length === 0 ? (
              <p style={{ fontSize:12,color:'#767683',fontFamily:'Tajawal, sans-serif',textAlign:'center',padding:'12px 0',margin:0 }}>{t('shipping.noCategoriesAvailable')}</p>
            ) : flatCats.map((c) => {
              const checked = classCategoryIds.includes(c.id)
              return (
                <label key={c.id} style={{ display:'flex',alignItems:'center',gap:8,padding:'6px 8px',borderRadius:8,cursor:'pointer',fontFamily:'Tajawal, sans-serif',fontSize:13,color:checked?'#000666':'#454652',fontWeight:checked?700:500 }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleClassCategory(c.id)} style={{ width:14,height:14,flexShrink:0 }}/>
                  <span style={{ paddingInlineStart: c.depth * 14 }}>{c.depth > 0 ? '— ' : ''}{c.name}</span>
                </label>
              )
            })}
          </div>
          <p style={{ fontFamily:'Tajawal, sans-serif',fontSize:11,color:'#767683',margin:'6px 0 0',lineHeight:1.6 }}>
            {t('shipping.assignedCategoriesHint')}
          </p>

          <div style={{ display:'flex',alignItems:'center',gap:10,margin:'14px 0' }}>
            <input type="checkbox" checked={classForm.isActive} onChange={(e) => setClassForm((p) => ({ ...p,isActive:e.target.checked }))} id="ca" style={{ width:16,height:16 }}/>
            <label htmlFor="ca" style={{ fontFamily:'Tajawal, sans-serif',fontSize:14,color:'#454652',cursor:'pointer' }}>{t('common.active')}</label>
          </div>
          <div style={{ display:'flex',gap:10 }}>
            <button onClick={submitClass} disabled={createClass.isPending||updateClass.isPending||!classForm.nameAr||classForm.baseCost===''} style={{ ...BTN, background:'#000666',color:'#fff', opacity: (!classForm.nameAr||classForm.baseCost==='')?0.5:1 }}><Save size={15}/>{createClass.isPending||updateClass.isPending?'...':t('common.save')}</button>
            <button onClick={() => setClassDialog({ open:false,mode:'create',cls:null })} style={{ ...BTN, background:'#F3F3F3',color:'#454652' }}>{t('common.cancel')}</button>
          </div>
        </DialogOverlay>
      )}

      <ConfirmDialog open={delClass.open} title={t('shipping.deleteClassTitle')} message={t('shipping.deleteClassMessage', { name: getLocalizedField(delClass.cls?.name, lang) })} variant="danger" loading={deleteClass.isPending} onConfirm={() => deleteClass.mutate(delClass.cls.id)} onCancel={() => setDelClass({ open:false,cls:null })}/>
      <ConfirmDialog open={delZone.open} title={t('shipping.deleteZoneTitle')} message={t('shipping.deleteZoneMessage', { name: delZone.zone?.name })} variant="danger" loading={deleteZone.isPending} onConfirm={() => deleteZone.mutate(delZone.zone.id)} onCancel={() => setDelZone({ open:false,zone:null })}/>
      <ConfirmDialog open={delMethod.open} title={t('shipping.deleteMethodTitle')} message={t('shipping.deleteMethodMessage', { name: getLocalizedField(delMethod.method?.name, lang) })} variant="danger" loading={deleteMethod.isPending} onConfirm={() => deleteMethod.mutate(delMethod.method.id)} onCancel={() => setDelMethod({ open:false,method:null })}/>
    </div>
  )
}

const LBL = { display:'block',fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:12,color:'#454652',marginBottom:6 }
const INP = { width:'100%',borderRadius:10,background:'#E2E2E2',border:'none',padding:'10px 14px',fontSize:14,fontFamily:'Tajawal, sans-serif',outline:'none',boxSizing:'border-box',color:'#1A1C1C' }
const BTN = { flex:1,padding:'11px 0',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8 }
